import User from './User';
import Location from './Location';

export enum PermissionsEnum {
  READONLY,
  PROVISION,
  WRITE,
  USER_CREATE,
  USER_UPDATE,
  USER_REMOVE,
  OWNER,
  VALVE_OPEN,
  VALVE_CLOSE
}

export type RoleType = 'fullaccess' | 'readonly' | 'provisioner';

export interface Role {
  roleKey: RoleType;
  permissions: PermissionsEnum[];
}

export const AccountRolesMapping: Role[] = [
  {
    roleKey: 'readonly',
    permissions: [ PermissionsEnum.READONLY ]
  },
  {
    roleKey: 'provisioner',
    permissions: [
      PermissionsEnum.USER_CREATE,
      PermissionsEnum.READONLY,
      PermissionsEnum.PROVISION,
    ]
  },
  {
    roleKey: 'fullaccess',
    permissions: [
      PermissionsEnum.USER_CREATE,
      PermissionsEnum.USER_REMOVE,
      PermissionsEnum.USER_UPDATE,
      PermissionsEnum.WRITE,
    ]
  },
]

export const LocationRolesMapping: Role[] = [
  {
    roleKey: 'readonly',
    permissions: [ PermissionsEnum.READONLY ]
  },
  {
    roleKey: 'fullaccess',
    permissions: [
      PermissionsEnum.VALVE_CLOSE,
      PermissionsEnum.VALVE_OPEN,
      PermissionsEnum.WRITE,
    ]
  },
]

export function getAccountPermissions(roleKey: RoleType): PermissionsEnum[] {
  const roles = AccountRolesMapping.find(role => role.roleKey === roleKey);
  if (!roles) {
    console.error(`Permission ${roleKey} not found`)
  }
  return roles?.permissions || [];
}

export function getLocationPermissions(roleKey: RoleType): PermissionsEnum[] {
  const roles = LocationRolesMapping.find(role => role.roleKey === roleKey);
  if (!roles) {
    console.error(`Permission ${roleKey} not found`)
  }
  return roles?.permissions || [];
}

export function getRoleFromPermissions(permissions: PermissionsEnum[]): RoleType | undefined {
  if (permissions.includes(PermissionsEnum.OWNER)) {
    return 'fullaccess';
  } else if (permissions.includes(PermissionsEnum.WRITE)) {
    return 'fullaccess';
  } else if (permissions.includes(PermissionsEnum.PROVISION)) {
    return 'fullaccess';
  } else if (permissions.includes(PermissionsEnum.READONLY)) {
    return 'readonly';
  } else {
    return undefined
  }
}

export function getAccountRoleFromPermissions(permissions: PermissionsEnum[]): string | undefined {
  if (permissions.includes(PermissionsEnum.OWNER)) {
    return 'owner';
  } else if (permissions.includes(PermissionsEnum.WRITE)) {
    return 'fullaccess';
  } else if (permissions.includes(PermissionsEnum.PROVISION)) {
    return 'provisioner';
  } else if (permissions.includes(PermissionsEnum.READONLY)) {
    return 'readonly';
  } else {
    return undefined
  }
}
export function getAccountRoleKeysFromPermissions(permissions: PermissionsEnum[]): string | undefined {
  if (permissions.includes(PermissionsEnum.OWNER)) {
    return 'owner';
  } else if (permissions.includes(PermissionsEnum.WRITE)) {
    return 'admin';
  } else if (permissions.includes(PermissionsEnum.PROVISION)) {
    return 'provisioner';
  } else if (permissions.includes(PermissionsEnum.READONLY)) {
    return 'readonly';
  } else {
    return undefined
  }
}

export interface AccountRole {
  accountId: string;
  roles: PermissionsEnum[];
}

export interface LocationRole {
  locationId: string;
  roles: PermissionsEnum[];
  isInherited?: boolean;
}

export interface UserRole {
  userId: string;
  roles: PermissionsEnum[];
  isInherited?: boolean;
}

export type LocationRoles = LocationRole[];

export const getPermissionKey = (permission: PermissionsEnum): string => {
  switch(permission) {
    case PermissionsEnum.OWNER:
      return 'perm-owner';
    case PermissionsEnum.READONLY:
      return 'perm-readonly';
    case PermissionsEnum.PROVISION:
      return 'perm-provision';
    case PermissionsEnum.USER_CREATE:
      return 'perm-user-create';
    case PermissionsEnum.USER_REMOVE:
      return 'perm-user-remove';
    case PermissionsEnum.USER_UPDATE:
      return 'perm-user-update';
    case PermissionsEnum.VALVE_CLOSE:
      return 'perm-valve-close';
    case PermissionsEnum.VALVE_OPEN:
      return 'perm-valve-open';
    case PermissionsEnum.WRITE:
      return 'perm-write';
  }
}

export function isAccountFullAccess(user?: User): boolean {
  if (!user) {
    return false;
  } else if (user.accountRole?.roles.includes(PermissionsEnum.OWNER)) {
    return true;
  } else if (
    user.accountRole?.roles.includes(PermissionsEnum.WRITE) &&
    user.accountRole?.roles.includes(PermissionsEnum.USER_CREATE) &&
    user.accountRole?.roles.includes(PermissionsEnum.USER_UPDATE) &&
    user.accountRole?.roles.includes(PermissionsEnum.USER_REMOVE))
  {
    return true;
  } else if (user.accountRole?.roles.includes(PermissionsEnum.PROVISION)) {
    return true;
  } else if (user.accountRole?.roles.includes(PermissionsEnum.READONLY)) {
    return false;
  } else {
    return false;
  }
}

export function canCreateAccount(user?: User): boolean {
  return (
    user?.accountRole?.roles.includes(PermissionsEnum.USER_CREATE) ||
    user?.accountRole?.roles.includes(PermissionsEnum.OWNER) ||
    false
  );
}

export function createAccountRolesList(user?: User): Role[] {
  const allRoles = AccountRolesMapping.filter(m => m.roleKey !== 'provisioner') //temp disable provisioner
  if (user?.accountRole?.roles){
    const rk = getAccountRoleKeysFromPermissions(user?.accountRole?.roles);
    switch(rk) {
      case 'provisioner':
      case 'readonly':
        return allRoles.filter(m => m.roleKey === 'readonly')
    }
  }
  return allRoles;
}

export function canUpdateAccount(user?: User): boolean {
  return (
    user?.accountRole?.roles.includes(PermissionsEnum.USER_UPDATE) ||
    user?.accountRole?.roles.includes(PermissionsEnum.OWNER) ||
    false
  );
}

export function canRemoveAccount(user?: User): boolean {
  return (
    user?.accountRole?.roles.includes(PermissionsEnum.USER_REMOVE) ||
    user?.accountRole?.roles.includes(PermissionsEnum.OWNER) ||
    false
  );
}

export function isLocationFullAccess(location?: Location): boolean {
  return  location?.roles?.includes(PermissionsEnum.WRITE) ||
          location?.roles?.includes(PermissionsEnum.OWNER) ||
          location?.roles?.includes(PermissionsEnum.PROVISION) ||
          false;
}

export function isLocationOwner(location?: Location): boolean {
  return location?.roles?.includes(PermissionsEnum.OWNER) || false;
}

export function getRoleRelevanceForSorting(permissions: PermissionsEnum[]): number {
  if (permissions.includes(PermissionsEnum.OWNER)) {
    return 1000;
  } else if (permissions.includes(PermissionsEnum.WRITE)) {
    return 100;
  } else if (permissions.includes(PermissionsEnum.READONLY)) {
    return 10;
  } else {
    return 0
  }
}